import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import RestoreFromTrashOutlinedIcon from '@mui/icons-material/RestoreFromTrashOutlined';
import { DataTable, EmptyState } from '@routes-plan/ui';
import StandardModal from '../../../components/modales/StandardModal';
import { useT } from '../../../i18n';
import { formatDay } from '../storage/StorageCleanup';

/** Clientes → Papelera: los clientes eliminados en los últimos 30 días, para recuperarlos. */
interface Erased {
  id: number;
  clientId: number;
  name: string;
  route: { id: number; name: string | null } | null;
  erasedBy: string | null;
  erasedAt: string | null;
  restoreUntil: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onRestored: (message: string, severity: 'success' | 'error') => void;
}

const ClientTrashModal: React.FC<Props> = ({ open, onClose, onRestored }) => {
  const t = useT();
  const [items, setItems] = useState<Erased[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/clients/erased');
      setItems(data?.data ?? []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const restore = async (e: Erased) => {
    setBusy(e.id);
    try {
      await axios.post(`/clients/erased/${e.id}/restore`);
      onRestored(t('clients.trash.restored', { name: e.name }), 'success');
      await load();
    } catch (error: any) {
      const code = error?.response?.data?.code as string | undefined;
      const known = ['EXPIRED', 'DUPLICATE_DOCUMENT', 'ALREADY_RESTORED'];
      onRestored(code && known.includes(code) ? t(`clients.trash.error.${code}` as 'clients.trash.error.EXPIRED') : t('clients.trash.error.generic'), 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <StandardModal open={open} onClose={onClose} title={t('clients.trash.title')} subtitle={t('clients.trash.subtitle')} maxWidth={900} loading={loading}>
      <Stack spacing={1.5} sx={{ minWidth: 0 }}>
        <DataTable<Erased>
          rows={items}
          getRowId={(e) => e.id}
          ariaLabel={t('clients.trash.title')}
          maxHeight={420}
          initialSort={{ columnId: 'until', direction: 'asc' }}
          columns={[
            { id: 'name', header: t('clients.trash.name'), value: (e) => e.name, sortable: true },
            { id: 'route', header: t('clients.trash.route'), value: (e) => e.route?.name ?? '—', sortable: true, hideBelow: 'sm' },
            { id: 'erasedAt', header: t('clients.trash.erasedAt'), value: (e) => e.erasedAt ?? '', render: (e) => formatDay(e.erasedAt), sortable: true, hideBelow: 'md' },
            { id: 'by', header: t('clients.trash.by'), value: (e) => e.erasedBy ?? '—', hideBelow: 'md' },
            { id: 'until', header: t('clients.trash.until'), value: (e) => e.restoreUntil ?? '', render: (e) => formatDay(e.restoreUntil), sortable: true },
          ]}
          rowActions={(e) => (
            <Button size="small" startIcon={<RestoreFromTrashOutlinedIcon />} disabled={busy !== null} onClick={() => restore(e)}>
              {t('clients.trash.restore')}
            </Button>
          )}
          emptyState={<EmptyState dense icon={<RestoreFromTrashOutlinedIcon />} title={t('clients.trash.empty')} />}
        />
        <Typography variant="body2" color="text.secondary">
          {t('clients.trash.note')}
        </Typography>
      </Stack>
    </StandardModal>
  );
};

export default ClientTrashModal;
